import BaseApi from '@/uptk/core/api/BaseApi';
import type { ModuleSpecDraft, ModuleSpecDraftInput } from '../data/contracts';
import { studioRequestId } from './requestId';
import { invalidateStudioState } from '../data/studioMutationFeedback';

type Price = { provider?: string | null; model?: string | null; estimatedCostChf?: number | null };
const estimates = new Map<string, Price>();
export function rememberStudioEstimate(scope: string, estimate: Price): void {
  estimates.set(scope, { provider: estimate.provider, model: estimate.model, estimatedCostChf: estimate.estimatedCostChf });
}
export async function executeEstimatedAnalysis<T>(dossier: string, importId: string, input: unknown): Promise<T> {
  const parameters = { importId, input };
  const response = await BaseApi.post('/api/v1/module-studio/dossiers/' + encodeURIComponent(dossier) + '/commands/analyse/prepare', { parameters });
  const quote = response.data as { available: boolean; estimate: Price; provider: string; model: string; approvalToken: string };
  const displayed = estimates.get(importId);
  if (!quote.available || !displayed || displayed.provider !== quote.provider || displayed.model !== quote.model || displayed.estimatedCostChf !== quote.estimate.estimatedCostChf)
    throw new Error('studio.command.cost_confirmation_required');
  return executeStudioCommand(dossier, 'analyse', { ...parameters, costApproval: quote.approvalToken });
}

/** ACCEPT-FAST: the dossier thread follows a LONG-CMD acceptance started anywhere (thread, version panel, plan). */
export type StudioTaskProgress = { id: string; command: string; status: string; step: string; percentage: number };
const taskListeners = new Map<string, Set<(task: StudioTaskProgress) => void>>();
export function subscribeStudioTask(dossier: string, listener: (task: StudioTaskProgress) => void): () => void {
  const listeners = taskListeners.get(dossier) ?? new Set();
  listeners.add(listener); taskListeners.set(dossier, listeners);
  return () => { listeners.delete(listener); if (!listeners.size) taskListeners.delete(dossier); };
}
/** COMPILE-ASYNC : le plan publie aussi la progression locale (compilation page par page) et celle de sa tâche. */
export function notifyStudioTask(dossier: string, task: StudioTaskProgress): void {
  taskListeners.get(dossier)?.forEach(listener => listener(task));
}
/** Statut HTTP du refus rendu par une tâche : celui du corps direct quand il est connu, sinon 409 métier ou 500 inconnu. */
export function studioTaskFailureStatus(errors: { code?: string; status?: number } | undefined): number {
  if (typeof errors?.status === 'number' && errors.status >= 400 && errors.status < 600) return errors.status;
  return (errors?.code ?? 'studio.command.failed') === 'studio.command.failed' ? 500 : 409;
}

export async function executeStudioCommand<T>(dossier: string, key: string, parameters: Record<string, unknown>): Promise<T> {
  const response = await BaseApi.post('/api/v1/module-studio/dossiers/' + encodeURIComponent(dossier) + '/commands/' + encodeURIComponent(key), {
    confirmed: true, parameters, requestId: studioRequestId(),
  });
  invalidateStudioState();
  // LONG-CMD : toute commande admise en tâche (202) est suivie jusqu'à son reçu terminal, sans succès anticipé.
  if (response.status === 202 && response.data && typeof (response.data as { id?: unknown }).id === 'string') {
    let task = response.data as StudioTaskProgress & { result?: T; errors?: { code?: string; status?: number } };
    notifyStudioTask(dossier, task);
    while (task.status === 'queued' || task.status === 'running') {
      await new Promise(resolve => setTimeout(resolve, 500));
      const next = await BaseApi.get('/api/v1/module-studio/dossiers/' + encodeURIComponent(dossier) + '/commands/tasks/' + task.id);
      task = next.data as typeof task;
      notifyStudioTask(dossier, task);
    }
    invalidateStudioState();
    if (task.status === 'failed') {
      // Same body as the direct refusal: a business code is a 409, an unknown worker failure stays a server error.
      const code = task.errors?.code ?? 'studio.command.failed';
      throw Object.assign(new Error(code), { response: { status: studioTaskFailureStatus(task.errors), data: task.errors } });
    }
    return task.result as T;
  }
  return response.data as T;
}

export function saveLocalCommand(key: 'texts' | 'targets' | 'bind' | 'replay', dossier: string, input: ModuleSpecDraftInput): Promise<ModuleSpecDraft> {
  return executeStudioCommand(dossier, key, { input });
}
/** COMPILE-ASYNC : reçu de « Compiler tous les écrans » ; le brouillon complet se relit, il ne transite plus par la tâche. */
export type StudioCompileReceipt = Omit<ModuleSpecDraft, 'spec' | 'layout'> & { unchanged: boolean; ignoredPieces: { pieceRef: string; fileName: string; kind: string }[] };
export function compileLocalCommand(dossier: string, parameters: { input: ModuleSpecDraftInput; baseVersion?: number; ignoredPieces?: unknown[] }): Promise<StudioCompileReceipt> {
  return executeStudioCommand(dossier, 'compile', parameters);
}
